import { InfoPlateVariant } from "fe-shared/src/components";

import {
  PickupRequestDetails,
  PickupRequestPriority,
  PickupRequestPriorityId,
  PickupRequestStatus,
} from "../types";

export const PICKUP_REQUEST_STATUS_ORDER: PickupRequestStatus[] = [
  PickupRequestStatus.Submitted,
  PickupRequestStatus.InReview,
  PickupRequestStatus.Approved,
  PickupRequestStatus.Invalid,
  PickupRequestStatus.Requested,
  PickupRequestStatus.Accepted,
  PickupRequestStatus.Completed,
];

// Follows TRANSACTION_STATUSES_CONFIG_MAP for every status the two share.
export const PICKUP_REQUEST_STATUS_VARIANT_MAP: Record<
  PickupRequestStatus,
  InfoPlateVariant
> = {
  [PickupRequestStatus.Submitted]: "neutral",
  [PickupRequestStatus.InReview]: "warning",
  [PickupRequestStatus.Approved]: "active",
  [PickupRequestStatus.Invalid]: "error",
  [PickupRequestStatus.Requested]: "purple",
  [PickupRequestStatus.Accepted]: "orange",
  [PickupRequestStatus.Completed]: "success-dark",
};

const DEFAULT_STATUS_VARIANT: InfoPlateVariant = "neutral";

// Widened to string on purpose: the backend may add a status before the FE knows it,
// and an unmapped code must degrade to a neutral chip rather than render undefined.
export const getPickupRequestStatusVariant = (
  status: PickupRequestStatus | string
): InfoPlateVariant =>
  PICKUP_REQUEST_STATUS_VARIANT_MAP[status as PickupRequestStatus] ??
  DEFAULT_STATUS_VARIANT;

const EDITABLE_STATUSES: PickupRequestStatus[] = [
  PickupRequestStatus.Submitted,
  PickupRequestStatus.Approved,
  PickupRequestStatus.Requested,
];

// Mirrors be-request-service's PickupRequestEditPolicy: InReview is editable only for an
// internal caller (the policy throws for anyone else), everyone else follows EDITABLE_STATUSES.
export const isPickupRequestEditable = (
  status: PickupRequestStatus,
  isCallerInternal: boolean
): boolean => {
  if (EDITABLE_STATUSES.includes(status)) {
    return true;
  }

  return isCallerInternal && status === PickupRequestStatus.InReview;
};

export const PICKUP_REQUEST_PRIORITY_ID_MAP: Record<
  PickupRequestPriority,
  PickupRequestPriorityId
> = {
  [PickupRequestPriority.Low]: PickupRequestPriorityId.Low,
  [PickupRequestPriority.Mid]: PickupRequestPriorityId.Mid,
  [PickupRequestPriority.High]: PickupRequestPriorityId.High,
};

export const toPickupRequestPriorityId = (
  priority: PickupRequestPriority
): PickupRequestPriorityId => PICKUP_REQUEST_PRIORITY_ID_MAP[priority];

const PICKUP_REQUEST_PRIORITY_CODE_MAP: Record<
  PickupRequestPriorityId,
  PickupRequestPriority
> = {
  [PickupRequestPriorityId.Low]: PickupRequestPriority.Low,
  [PickupRequestPriorityId.Mid]: PickupRequestPriority.Mid,
  [PickupRequestPriorityId.High]: PickupRequestPriority.High,
};

// Returns undefined rather than asserting: an id outside 1-3 has no code, and
// claiming one would push a bad value into the create/update payload.
export const fromPickupRequestPriorityId = (
  id: PickupRequestPriorityId
): PickupRequestPriority | undefined => PICKUP_REQUEST_PRIORITY_CODE_MAP[id];

export const canSendPickupRequest = (
  request?: Pick<
    PickupRequestDetails,
    "status" | "targetCompanyId" | "assignedCarrierId"
  > | null
): boolean =>
  request?.status === PickupRequestStatus.Approved &&
  !!request.targetCompanyId &&
  !!request.assignedCarrierId;

export const canResendPickupRequest = (
  request?: Pick<
    PickupRequestDetails,
    "status" | "targetCompanyId" | "assignedCarrierId"
  > | null
): boolean =>
  request?.status === PickupRequestStatus.Requested &&
  !!request.targetCompanyId &&
  !!request.assignedCarrierId;

export type PickupRequestAssignmentRole = "portal" | "hauler";

const CARRIER_WITHDRAW_STATUSES: Record<
  PickupRequestAssignmentRole,
  PickupRequestStatus[]
> = {
  portal: [
    PickupRequestStatus.InReview,
    PickupRequestStatus.Approved,
    PickupRequestStatus.Requested,
  ],
  hauler: [PickupRequestStatus.Approved, PickupRequestStatus.Requested],
};

export const canWithdrawCarrier = (
  status: PickupRequestStatus,
  role: PickupRequestAssignmentRole
): boolean => CARRIER_WITHDRAW_STATUSES[role].includes(status);

export const canWithdrawHauler = (
  status: PickupRequestStatus,
  role: PickupRequestAssignmentRole
): boolean =>
  role === "portal" &&
  (status === PickupRequestStatus.InReview ||
    status === PickupRequestStatus.Approved ||
    status === PickupRequestStatus.Requested);

export const canAssignPortalUser = (status: PickupRequestStatus): boolean =>
  status === PickupRequestStatus.Submitted ||
  status === PickupRequestStatus.InReview ||
  status === PickupRequestStatus.Approved ||
  status === PickupRequestStatus.Requested ||
  status === PickupRequestStatus.Accepted ||
  status === PickupRequestStatus.Completed;

export const canEditAssignment = (
  status: PickupRequestStatus,
  role: PickupRequestAssignmentRole
): boolean =>
  canWithdrawCarrier(status, role) || canWithdrawHauler(status, role);
